import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useContext } from 'react';
import { SDKStudy } from '@axon/rosetta-sdk';
import { useLocalizeMessage, useToastNotification, Spinner } from 'libs.nucleus';
import { ButtonWithTooltip, useBooleanState } from 'libs.react';
import { IrbContext } from '../../contexts';
import { useCreateIrbPackages, useEnvironmentCortexToken, useStudyVersion } from '../../hooks';
import { getValidEnvironments } from '../../utils/environment_utils';
import { CriteriaSelectionModal } from '../criteria_selection_modal';
export const CreatePackagetAction = () => {
    const t = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const { getEnvironments } = useContext(IrbContext);
    const { versions, isLoading: isStudyVersionLoading } = useStudyVersion();
    const environments = getEnvironments();
    const validEnvironments = getValidEnvironments(environments);
    const isVersionCreated = versions.size > 0 && !isStudyVersionLoading;
    const { mutate: createIrbPackages } = useCreateIrbPackages();
    const { mutate: getAuthToken } = useEnvironmentCortexToken();
    const [isProcessing, startProcessing, stopProcessing] = useBooleanState(false);
    const [isModalOpen, openModal, closeModal] = useBooleanState(false);
    const handleCreatePackage = useCallback((props) => {
        try {
            const { selectedLocales, selectedEnvironment, selectedDevice, isCoverPageIncluded, includeClinicianInterviewClinros, selectedViewport, } = props;
            if (!selectedEnvironment) {
                throw new Error('Environment not selected');
            }
            const _env = validEnvironments.find((env) => env.id === selectedEnvironment.id);
            const buildVersion = _env?.workflows?.[0].studyConfigVersion;
            if (!buildVersion) {
                throw new Error('Build version not found');
            }
            const deployedStudyConfig = Array.from(versions.values()).find((version) => version.version?.toString() === selectedEnvironment.metadata?.studyConfigVersion?.toString());
            const studyPinnedProductTemplate = deployedStudyConfig
                ? SDKStudy.fromRosetta(deployedStudyConfig.data).pinnedProductTemplate
                : undefined;
            if (!studyPinnedProductTemplate) {
                addNotification({
                    title: t('Error'),
                    subtitle: t('Internal error: study pinned product template not found'),
                    type: 'error',
                });
                return;
            }
            const studyUuid = selectedEnvironment.metadata?.packages?.find(({ type }) => type === 'studyConfig')?.resourceId;
            if (!studyUuid) {
                throw new Error('Missing studyUuid');
            }
            startProcessing();
            getAuthToken({ envId: selectedEnvironment.id }, {
                onSuccess: (authToken) => {
                    const packages = selectedLocales.map((locale) => ({
                        buildVersion,
                        locale,
                        modality: selectedDevice,
                        viewport: selectedViewport,
                        isCoverPageIncluded,
                        includeClinicianInterviewClinros,
                    }));
                    createIrbPackages({
                        packages,
                        authToken,
                        studyUuid,
                        studyPinnedProductTemplate,
                        envId: selectedEnvironment.id,
                    }, {
                        onSuccess: () => {
                            closeModal();
                        },
                        onSettled: () => {
                            stopProcessing();
                        },
                    });
                },
                onError: () => {
                    stopProcessing();
                },
            });
        }
        catch (error) {
            console.error(error);
            addNotification({
                title: t('Error'),
                subtitle: error.message || t('An unknown error occurred'),
                type: 'error',
            });
        }
    }, [
        addNotification,
        closeModal,
        createIrbPackages,
        getAuthToken,
        startProcessing,
        stopProcessing,
        t,
        validEnvironments,
        versions,
    ]);
    return (_jsxs(_Fragment, { children: [_jsx(ButtonWithTooltip, { variant: 'primary', onClick: openModal, label: '', content: _jsxs("div", { className: 'flex items-center gap-1', children: [isStudyVersionLoading && _jsx(Spinner, { size: 'sm' }), " ", t('Create package')] }), disabled: !isVersionCreated, tooltip: t('To create a package, you must first create a Version of your study.'), enableTooltip: !isVersionCreated, tooltipPlacement: 'bottom' }), isModalOpen && (_jsx(CriteriaSelectionModal, { onModalClose: closeModal, onHandleCreatePackage: handleCreatePackage, isProcessing: isProcessing, versions: versions }))] }));
};
